import React, { useMemo, useState } from 'react';
import { Button, Modal, Select, Tooltip } from 'antd';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment';
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Eye,
  MousePointerClick,
  Percent,
} from 'lucide-react';

import CreativeTable from '../../../../components/creatives/components/CreativeCTRTable';
import SegmentsTable from '../../../../components/segments/SegmentsTable';
import { MenuItemTextColor } from '../../../../../../../shared/globalStyling/styledText';
import {
  DateRangeMenu,
  DualAxisAreaChart,
  EntityBreakdownCard,
  KpiBar,
  KpiCard,
  StatusPill,
  formatDateOnly,
} from '../../../../../../../shared/designSystem';
import {
  computeCTR,
  formatNumber2SigFig,
  formatWholeNumber,
} from '../../../../../../../../core/utils/campaigns';
import { tagStatusConfig } from '../../../../utils';

// =============================================================================
// LineItemsModal — "View Charts" Modal for a single line item.
//
// v2 facelift: full migration to shared design-system primitives.
//   · Trigger:   `View Charts` icon-button (Lucide `BarChart3`) wrapped in
//                a Tooltip. Keeps v1's parent-owned visibility state.
//   · Modal:     AntD `<Modal>` with `rootClassName="mjl-ds"` so its portal
//                picks up the v2 overrides (Modals render in document.body,
//                outside any consumer's `.mjl-ds` wrapper).
//   · Title:     line-item name + shared `<StatusPill>` via `tagStatusConfig`
//                lookup (same Line-Items-specific vocabulary as LineItemsCard).
//   · KPIs:      `<KpiBar>` + 4 `<KpiCard>`s — Flight Dates (custom body
//                with progress bar) / Impressions / Clicks / CTR.
//   · Chart:     shared `<DualAxisAreaChart>` (Recharts). Replaces the
//                BizCharts `DualAxisChart` + its internal date-picker.
//   · Creatives: `<EntityBreakdownCard>` shell, table-only (no breakdown
//                ring). Mirrors the Display tab Creatives card's look.
//   · Segments:  same `<EntityBreakdownCard>` shell.
//
// Preserved verbatim: byDate → summary reduction logic, data-shape mapping
// for CreativeTable (computeCTR + `value: c.creativeName`), status vocabulary,
// flight-progress math. No behavioral changes.
//
// Range picker: shared `<DateRangePicker>` (segmented pills + canonical
// secondary preset shortcuts) drives both the KPI bar and the chart via a
// single `filteredByDate` memo. `defaultDateRange =
// [summary.startDate, summary.endDate]` clamps Custom-calendar cells to the
// line item flight; today-relative presets are pass-through and may produce
// an empty filter when they fall outside flight. `<Modal destroyOnClose>`
// resets picker state on close.
// =============================================================================

const AXIS_OPTIONS = ['Impressions', 'Clicks'];

const flightProgress = (startDate, endDate) => {
  const a = moment(endDate);
  const b = moment(startDate);
  const total = a.diff(b, 'days');
  const progress = moment().diff(b, 'days');
  if (total <= 0 || progress >= total) return 100;
  if (progress <= 0) return 0;
  return Math.floor((progress / total) * 100);
};

const LineItemsModal = ({
  byDate = [],
  summary = {
    startDate: '2025-01-01',
    endDate: '2026-01-01',
    impressions: 0,
    clicks: 0,
    ctr: 0,
  },
  creatives = [],
  publishers = [], // eslint-disable-line no-unused-vars
  segments = [],
  name = 'Line Item',
  status = 'PLAY',
}) => {
  const [visible, setVisible] = useState(false);
  const [rangeType, setRangeType] = useState('Custom');
  const [byRangeDate, setByRangeDate] = useState(null);
  const [leftY, setLeftY] = useState('Impressions');
  const [rightY, setRightY] = useState('Clicks');

  const filteredByDate = useMemo(() => {
    const start = byRangeDate ? byRangeDate[0] : moment(summary.startDate);
    const end = byRangeDate ? byRangeDate[1] : moment(summary.endDate);
    return byDate.filter((d) => {
      const m = moment(d.date);
      return m.isValid() && m.isSameOrAfter(start, 'day') && m.isSameOrBefore(end, 'day');
    });
  }, [byDate, byRangeDate, summary.startDate, summary.endDate]);

  const summaryData = useMemo(
    () =>
      filteredByDate.reduce(
        (acc, d) => {
          acc.impressions += d.impressions;
          acc.clicks += d.clicks;
          acc.ctr = computeCTR(acc.clicks, acc.impressions);
          return acc;
        },
        { impressions: 0, clicks: 0, ctr: 0 }
      ),
    [filteredByDate]
  );

  const dualChartData = useMemo(
    () =>
      filteredByDate
        .map((d) => ({
          ...d,
          xAxis: moment(d.date).isValid()
            ? moment(d.date).format('YYYY-MM-DD')
            : d.date,
        }))
        .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis))),
    [filteredByDate]
  );

  const progressPercent = flightProgress(summary.startDate, summary.endDate);
  const pillConfig = tagStatusConfig[status] || tagStatusConfig.PLAY;

  return (
    <>
      <Tooltip title="View Charts" overlayClassName="mjl-ds">
        <Button
          type="link"
          onClick={() => setVisible(true)}
          icon={<BarChart3 size={16} strokeWidth={2} />}
          aria-label="View Charts"
        />
      </Tooltip>

      <Modal
        visible={visible}
        width="85%"
        footer={null}
        onCancel={() => setVisible(false)}
        destroyOnClose
        rootClassName="mjl-ds"
        title={
          <div className="page-header__title-row">
            <StatusPill {...pillConfig} />
            <h2 className="page-header__title" title={name}>
              {name}
            </h2>
          </div>
        }
      >
        {/* Performance section — groups KPI strip + chart into one card,
            mirroring the Summary / Basket Items V2 / Sign Ups V2 Performance
            pattern. Without this wrapper the KPI row + chart render as two
            disconnected blocks in the modal body. */}
        <section className="campaign-overview__performance">
          <header className="campaign-overview__performance-head">
            <div className="campaign-overview__performance-eyebrow mono-eyebrow">
              Performance
            </div>
            <DateRangeMenu
              rangeType={rangeType}
              onRangeTypeChange={setRangeType}
              value={byRangeDate}
              onChange={setByRangeDate}
              defaultDateRange={[moment(summary.startDate), moment(summary.endDate)]}
              timezone="UTC"
            />
          </header>

          <KpiBar>
            <KpiCard
              icon={<Calendar size={16} />}
              iconTint="neutral"
              label="Flight Dates"
              labelTooltip="Flight Dates refers to the Start & End Dates of this line item"
            >
              <div className="campaign-overview__flight">
                <div className="campaign-overview__flight-dates">
                  <span>{formatDateOnly(summary.startDate, '—')}</span>
                  <ArrowRight size={14} className="campaign-overview__flight-arrow" />
                  <span>{formatDateOnly(summary.endDate, '—')}</span>
                </div>
                <div className="campaign-overview__flight-progress">
                  <div
                    className="campaign-overview__flight-progress-bar"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="campaign-overview__flight-meta">
                  {progressPercent}% elapsed
                </div>
              </div>
            </KpiCard>
            <KpiCard
              icon={<Eye size={16} />}
              iconTint="primary"
              label="Impressions"
              value={formatWholeNumber(summaryData.impressions)}
            />
            <KpiCard
              icon={<MousePointerClick size={16} />}
              iconTint="emerald"
              label="Clicks"
              value={formatWholeNumber(summaryData.clicks)}
            />
            <KpiCard
              icon={<Percent size={16} />}
              iconTint="purple"
              label="CTR"
              value={formatNumber2SigFig(summaryData.ctr)}
              valueSuffix="%"
            />
          </KpiBar>

          {!isEmpty(dualChartData) && (
            <div className="campaign-overview__chart">
              <div className="campaign-overview__chart-head">
                <div className="campaign-overview__chart-eyebrow mono-eyebrow">
                  Trend
                </div>
                <div className="campaign-overview__axis-row">
                  <div className="campaign-overview__axis-field">
                    <span className="campaign-overview__axis-label mono-eyebrow">
                      <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
                      Left axis
                    </span>
                    <Select
                      onSelect={setLeftY}
                      className="campaign-overview__axis-select"
                      value={leftY}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      popupClassName="mjl-ds"
                    >
                      {AXIS_OPTIONS.filter((op) => op !== rightY).map((option) => (
                        <Select.Option key={option} value={option}>
                          <MenuItemTextColor text={startCase(option)} color="#7dafff" />
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
                    <span className="campaign-overview__axis-label mono-eyebrow">
                      <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
                      Right axis
                    </span>
                    <Select
                      onSelect={setRightY}
                      className="campaign-overview__axis-select"
                      value={rightY}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      popupClassName="mjl-ds"
                    >
                      {AXIS_OPTIONS.filter((op) => op !== leftY).map((option) => (
                        <Select.Option key={option} value={option}>
                          <MenuItemTextColor text={startCase(option)} color="#92d5a7" />
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
              <div className="campaign-overview__chart-body">
                <DualAxisAreaChart
                  leftY={leftY}
                  rightY={rightY}
                  dualChartData={dualChartData}
                />
              </div>
            </div>
          )}
        </section>

        {/* Creatives + Segments sections — EntityBreakdownCard shells,
            no breakdown ring (table-only), identical look to the Display
            tab's Creatives / Segments cards. */}
        {!isEmpty(creatives) && (
          <EntityBreakdownCard
            title="Creatives"
            table={
              <CreativeTable
                creativeData={creatives.map((c) => ({
                  ...c,
                  value: c.creativeName,
                  ctr: computeCTR(c.clicks, c.impressions),
                }))}
              />
            }
          />
        )}

        {!isEmpty(segments) && (
          <EntityBreakdownCard
            title="Segments"
            table={<SegmentsTable segmentsData={segments} />}
          />
        )}
      </Modal>
    </>
  );
};

export default LineItemsModal;
